export const services = [
  {
    number: '01',
    title: 'Custom apps & operations platforms',
    description: 'Purpose-built software for daily work — management platforms, portals, shared company tooling, and mobile-first apps shaped to the operation.',
    proof: 'Daily workflows / portals / permissions',
  },
  {
    number: '02',
    title: 'Automation',
    description: 'Turn repetitive steps, handoffs, approvals, and follow-up into dependable workflows that keep moving without constant attention.',
    proof: 'Routing / approvals / recurring work',
  },
  {
    number: '03',
    title: 'Integrations',
    description: 'Connect the tools already in use so information moves once, stays current, and reaches the people and systems that need it.',
    proof: 'APIs / data sync / tool handoffs',
  },
  {
    number: '04',
    title: 'Data & dashboards',
    description: 'Turn recurring exports and operational data into clear reporting, alerts, and decision-ready views.',
    proof: 'Imports / reporting / alerts',
  },
  {
    number: '05',
    title: 'AI assistants',
    description: 'Add permission-aware assistants that can explain the operation, find answers, and carry out clearly bounded work.',
    proof: 'Answers / context / bounded actions',
  },
  {
    number: '06',
    title: 'Websites & deployment',
    description: 'Public websites and customer experiences connected to business data, with dependable Cloudflare delivery and ongoing improvement when needed.',
    proof: 'Web / data / edge delivery',
  },
] as const;

export const systemStory = {
  inputs: [
    { title: 'Requests arrive everywhere', detail: 'Email, forms, messages, and verbal follow-up' },
    { title: 'The same data gets re-entered', detail: 'Spreadsheets and tools drift out of sync' },
    { title: 'Progress depends on chasing', detail: 'Owners and teams assemble the answer by hand' },
  ],
  engine: {
    title: 'One operating layer',
    description: 'Apps, rules, data, permissions, and integrations shaped around how the business actually runs.',
    proof: 'Mapped to the operation — not forced into a generic template',
  },
  outcomes: [
    { title: 'Work reaches the right person', detail: 'Clear ownership, routing, and next actions' },
    { title: 'Reporting stays current', detail: 'Connected data becomes useful operating context' },
    { title: 'Routine follow-up completes', detail: 'Automations handle the repeatable parts' },
  ],
} as const;

export const projects = {
  portfolioCommand: {
    title: 'Portfolio Operations Platform — One private view across the portfolio',
    summary: 'A private management platform spanning companies, websites, and apps, with granular permissions, aggregate operational snapshots, and a permission-aware read-only assistant.',
    details: ['Company and property scopes', 'Permission-aware aggregate views', 'Read-only portfolio assistant'],
  },
  healthTracker: {
    title: 'Routine & Wellness Companion App — Daily routines, made dependable',
    summary: 'A mobile-first companion app with deterministic schedules, reminders, logging, and a read-only assistant designed for quick daily use.',
    details: ['Deterministic schedule engine', 'Profile-aware logging and reminders', 'Read-only assistant boundary'],
  },
  automotiveOperations: {
    title: 'Inventory & Sales Operations — From recurring exports to answers',
    summary: 'A local-first dashboard that turns recurring business exports into sales, inventory, hot-seller, data-quality, and action views without requiring a second entry workflow.',
    details: ['Safe repeat imports and validation', 'Sales and inventory views', 'Source freshness and data quality'],
  },
  connectedBrandOperations: {
    title: 'E-Commerce & Brand Operations — Operational data behind the customer experience',
    summary: 'An operations and marketing ecosystem where inventory data can feed public site content and connected workflows keep the digital experience tied to the operation.',
    details: ['Operations-backed public content', 'Inventory-fed data snapshots', 'Connected deployment workflow'],
  },
  sharedCompanyOperations: {
    title: 'Cross-Company Reporting — Shared reporting in one controlled view',
    summary: 'An internal shared operations app with multi-company aggregate reporting in one permission-aware surface.',
    details: ['Shared app, separated company scope', 'Aggregate reporting surfaces', 'Permission-aware access'],
  },
} as const;

export const processSteps = [
  {
    number: '01',
    title: 'Understand the operation',
    description: 'Map the manual steps, scattered data, and tools that do not fit before discussing software.',
  },
  {
    number: '02',
    title: 'Design the system',
    description: 'Show the screens, data flow, and automation boundaries before the build is locked.',
  },
  {
    number: '03',
    title: 'Build and connect',
    description: 'Build the app, wire integrations, and move representative data through it early.',
  },
  {
    number: '04',
    title: 'Deploy and improve',
    description: 'Ship with owner access and documentation, then refine against actual workflows when ongoing support is part of the engagement.',
  },
] as const;
